import React, { FC, useCallback } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useHistory } from 'react-router';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/reducers';
import { stateScheduleReservation } from '../../../store/reducers/schedule/scheduleReservationStateReducer';
import googleCalendarIcon from '../style/img/google-calendar.svg';

dayjs.extend(utc);
dayjs.extend(timezone);

interface Props {
  modalShow: boolean;
  onModalShow: () => void;
  onRefresh: () => void;
}

const ScheduleReservationCompletePop: FC<Props> = ({ modalShow, onModalShow, onRefresh }) => {
  const history = useHistory();

  const { scheduleCompleteReservation, scheduleReservation } = useSelector<RootState, stateScheduleReservation>(
    (state) => state.schedule.scheduleReservationState,
  );

  const onHistoryPageForward = useCallback(() => {
    history.push('/history');
  }, [history]);

  const onCloseModal = useCallback(() => {
    onModalShow();
    onRefresh();
  }, [onRefresh, onModalShow]);

  const buildGoogleCalendarUrl = (params: { text: string; dates: string; location: string; details: string }) => {
    return (
      'https://calendar.google.com/calendar/render?action=TEMPLATE' +
      `&text=${encodeURIComponent(params.text)}` +
      `&dates=${encodeURIComponent(params.dates)}` +
      `&ctz=${encodeURIComponent('Asia/Tokyo')}` +
      `&location=${encodeURIComponent(params.location)}` +
      `&details=${encodeURIComponent(params.details)}`
    );
  };

  const onClickGoogleCalendarAdd = useCallback(() => {
    const myPageReservationHistoryUrl = `${window.location.origin}/history`;
    const scheduleDate = dayjs(scheduleReservation.infoDate).tz('Asia/Tokyo').format('YYYYMMDD');
    const endDate =
      scheduleReservation.contentEndTime === '0000'
        ? dayjs(scheduleDate).add(1, 'day').format('YYYYMMDD')
        : scheduleDate;
    const dates = `${scheduleDate}T${scheduleReservation.contentStartTime}00/${endDate}T${scheduleReservation.contentEndTime}00`;
    const location = `${scheduleCompleteReservation.clubName}, ${scheduleCompleteReservation.clubAddress}`;
    const details = `${scheduleCompleteReservation.contentsName}\nスペース\u3000${scheduleCompleteReservation.eqsName}\n\n<a href="${myPageReservationHistoryUrl}">予約の詳細</a>`;
    window.open(
      buildGoogleCalendarUrl({
        text: scheduleCompleteReservation.contentsName,
        dates,
        location,
        details,
      }),
      '_blank',
      'noopener,noreferrer',
    );
  }, [scheduleCompleteReservation]);

  return (
    <Modal
      show={modalShow}
      onHide={onCloseModal}
      animation
      dialogClassName="p-modal-member__dialog p-modal-member__dialog--width880 schedule"
      contentClassName="p-modal-member__content"
    >
      <Modal.Body className="p-modal-member__body">
        <button type="button" className="p-modal-member__close-button" aria-label="閉じる" onClick={onCloseModal} />
        <div className="p-modal-member__container">
          <div className="p-modal-member__header">
            {scheduleCompleteReservation.registerFlg === 1 ? (
              <>
                <div className="p-modal-member__title">
                  プログラム予約を
                  <br className="u-sp-only" />
                  完了しました。
                </div>
              </>
            ) : (
              <>
                <div className="p-modal-member__title">
                  キャンセル待ちを
                  <br className="u-sp-only" />
                  登録しました。
                </div>
              </>
            )}
          </div>
          <div className="p-modal-member__inner p-modal-member__inner--padding-x80 u-gap15">
            {scheduleCompleteReservation.registerFlg === 1 ? (
              <>
                <div className="p-modal-member__text u-pb15">
                  プログラム予約が完了しました。
                  <br />
                  ご登録のメールアドレス宛に、ご予約内容をお送りしております。
                  <br />
                  また、ご予約内容は予約履歴一覧から確認、変更、キャンセルが可能です。
                </div>
              </>
            ) : (
              <>
                <div className="p-modal-member__text u-pb15">
                  キャンセルが発生した場合、ご登録のメールアドレス宛にご連絡いたします。
                  <br />
                  なお、通知後の予約は自動では行われません。お客様ご自身で予約のお手続きをお願いいたします。また、ご登録内容は予約履歴一覧から確認、キャンセル待ち解除が可能です。
                </div>
              </>
            )}
            <div className="p-reservation-card u-sp-gap10">
              <div className="p-reservation-card__header p-reservation-card__header--modal">
                <div className="p-reservation-card__row-text">
                  {scheduleCompleteReservation.clubName}
                  {scheduleCompleteReservation.annexFlag === 1 ? (
                    <span className="c-genre-label c-genre-label--large">別館</span>
                  ) : (
                    <></>
                  )}
                </div>
                <div className="p-reservation-card__title">{scheduleCompleteReservation.contentsName}</div>
              </div>

              <div className="p-reservation-card__details p-reservation-card__details--padding-x40">
                <dl className="p-detail-list u-gap7">
                  <div className="p-detail-list__item">
                    <dt className="p-detail-list__label">実施日時</dt>
                    <dd className="p-detail-list__value">{scheduleCompleteReservation.reservationDateTime}</dd>
                  </div>
                  <div className="p-detail-list__accordion-box">
                    <div className="p-detail-list__accordion-label">店舗情報</div>
                    <div className="p-detail-list__accordion-value">
                      {scheduleCompleteReservation.clubName}
                      {scheduleCompleteReservation.annexFlag === 1 ? (
                        <span className="c-genre-label c-genre-label--large">別館</span>
                      ) : (
                        <></>
                      )}
                    </div>
                  </div>
                  {scheduleCompleteReservation.registerFlg === 1 ? (
                    <>
                      <div className="p-detail-list__item">
                        <dt className="p-detail-list__label">スペース</dt>
                        <dd className="p-detail-list__value">
                          {scheduleCompleteReservation.spaceName
                            ? `${scheduleCompleteReservation.eqsName}・${scheduleCompleteReservation.spaceName}`
                            : scheduleCompleteReservation.eqsName}
                        </dd>
                      </div>
                      {scheduleCompleteReservation.companionShowFlg ? (
                        <div className="p-detail-list__item">
                          <dt className="p-detail-list__label">同行者</dt>
                          <dd className="p-detail-list__value">{scheduleCompleteReservation.companionNum}名</dd>
                        </div>
                      ) : (
                        <></>
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                </dl>
              </div>
            </div>

            <section className="p-notice ">
              <div className="p-notice__text-group">
                <div className="p-notice__text">キャンセル可能期限</div>
                <div className="p-notice__strong">{scheduleCompleteReservation.cancelTime}まで</div>
              </div>
              {scheduleCompleteReservation.registerFlg === 1 ? (
                <div className="p-notice__text-group">
                  <button type="button" className="p-notice__cancel-link c-link" onClick={onClickGoogleCalendarAdd}>
                    <img
                      src={googleCalendarIcon}
                      alt="Googleカレンダーに追加する"
                      className="p-notice__google-calendar-icon"
                    />
                    <span className="p-notice__google-calendar-text">Googleカレンダーに追加する</span>
                  </button>
                </div>
              ) : (
                <></>
              )}
            </section>
          </div>
          <div className="p-modal-member__button-area">
            <button type="button" className="c-button c-button--secondary c-button--large" onClick={onCloseModal}>
              戻る
            </button>
            <button type="button" className="c-button c-button--primary c-button--large" onClick={onHistoryPageForward}>
              予約履歴一覧へ
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ScheduleReservationCompletePop;
